import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ProjectService } from '../../services/ProjectService/project';
import { ProjectResponse } from '../../models/project.dto';
import { extractErrorMessage } from '../../shared/error-utils';
import { ErrorBanner } from '../../shared/components/error-banner/error-banner';

@Component({
  selector: 'app-select-project-dialog',
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatIconModule,
    ErrorBanner,
  ],
  templateUrl: './select-project-dialog.html',
  styleUrl: './select-project-dialog.scss',
})
export class SelectProjectDialog implements OnInit {
  projects = signal<ProjectResponse[]>([]);
  isLoading = signal(false);
  errorMessage = signal('');

  selection: ProjectResponse | string = '';

  constructor(private dialogRef: MatDialogRef<SelectProjectDialog>, private projectService: ProjectService) {}

  ngOnInit(): void {
    this.isLoading.set(true);
    this.projectService.list(0, 100).subscribe({
      next: (res) => {
        this.isLoading.set(false);
        // Only ACTIVE projects can take a new request — the backend rejects the rest with
        // PROJECT_NOT_ACTIVE, so offering them here would just lead to a guaranteed 400.
        this.projects.set(res.data.filter((p) => p.status === 'ACTIVE'));
      },
      error: (err: HttpErrorResponse) => {
        this.isLoading.set(false);
        this.errorMessage.set(extractErrorMessage(err, 'Failed to load projects.'));
      },
    });
  }

  get filteredProjects(): ProjectResponse[] {
    const term = typeof this.selection === 'string' ? this.selection.trim().toLowerCase() : '';
    if (!term) {
      return this.projects();
    }
    return this.projects().filter(
      (p) => p.name.toLowerCase().includes(term) || p.code.toLowerCase().includes(term)
    );
  }

  displayProject = (project: ProjectResponse | string): string => {
    if (!project) {
      return '';
    }
    return typeof project === 'string' ? project : `${project.code} — ${project.name}`;
  };

  get canSubmit(): boolean {
    return typeof this.selection === 'object' && !!this.selection;
  }

  cancel(): void {
    this.dialogRef.close();
  }

  submit(): void {
    if (!this.canSubmit) {
      return;
    }
    this.dialogRef.close((this.selection as ProjectResponse).id);
  }
}
